/**
 * Submit-for-review button for the response outline.
 *
 * Owner-only. Disabled until every section is approved. Click opens a
 * reviewer picker dialog; on confirm calls the artifact-reviews store.
 *
 * When it is blocked, the reason is rendered as visible text beside the button
 * and linked to it via `aria-describedby` — the same pattern as the Analyse Bid
 * gate in `BidDetailsPage`. This deliberately does NOT use a tooltip: the
 * Button base sets `disabled:pointer-events-none`, so a disabled button never
 * receives hover, and a `disabled` button is not focusable either, so a
 * hover/focus-triggered explanation is unreachable by keyboard and silent to
 * screen readers. Visible text needs neither.
 */

import { useMemo, useState } from 'react'
import { Send, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import { useArtifactReviewsStore } from '@/stores/artifact-reviews-store'
import { useResponsesStore } from '@/stores/responses-store'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'
import { toast } from 'sonner'

export interface SubmitForReviewButtonProps {
  artifactId: string
  bidMembers: BidMemberInfo[]
  currentUserId: string | undefined
  canSubmit: boolean
  /**
   * Why submission is blocked, or `null` when it is allowed. Owned by the
   * calling page so it can name its own units — the outline counts sections,
   * the questionnaire counts questions — and state how many are outstanding
   * rather than restating the rule.
   */
  disabledReason: string | null
}

export function SubmitForReviewButton({
  artifactId,
  bidMembers,
  currentUserId,
  canSubmit,
  disabledReason,
}: SubmitForReviewButtonProps) {
  const [open, setOpen] = useState(false)
  const [reviewerId, setReviewerId] = useState<string>('')

  const submitting = useArtifactReviewsStore((s) => s.submitting[artifactId] ?? false)
  const submit = useArtifactReviewsStore((s) => s.submit)
  const setArtifactInCache = useResponsesStore((s) => s.upsertArtifact)

  // Reviewer candidates: every bid member who isn't the current user.
  const candidates = useMemo(
    () => bidMembers.filter((m) => m.userId !== currentUserId),
    [bidMembers, currentUserId],
  )

  if (!canSubmit) return null

  const disabled = disabledReason !== null
  const hintId = `submit-review-hint-${artifactId}`

  const handleConfirm = async () => {
    if (!reviewerId) return
    try {
      const res = await submit(artifactId, reviewerId)
      setArtifactInCache(res.artifact)
      setOpen(false)
      setReviewerId('')
      toast.success('Sent for review')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit for review'
      toast.error(msg)
    }
  }

  const trigger = (
    <Button
      size="sm"
      onClick={() => setOpen(true)}
      disabled={disabled}
      className="gap-2"
      aria-describedby={disabled ? hintId : undefined}
    >
      <Send className="h-4 w-4" />
      Submit for review
    </Button>
  )

  return (
    <>
      {disabled ? (
        <div className="flex items-center gap-2">
          <span id={hintId} className="text-meta">
            {disabledReason}
          </span>
          {trigger}
        </div>
      ) : (
        // Enabled only: an interactive button does receive hover, so the
        // tooltip is a real affordance here rather than a dead one.
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="bottom">
              Send this response to a reviewer for sign-off.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit response for review</DialogTitle>
            <DialogDescription>
              Pick a reviewer. They'll be notified and can leave comments,
              approve, or request changes. Editing is locked until they decide.
            </DialogDescription>
          </DialogHeader>

          {candidates.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">
              No other bid members available to review. Add a team member to this
              bid first.
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Reviewer</label>
              <Select value={reviewerId} onValueChange={setReviewerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a reviewer..." />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((m) => (
                    <SelectItem key={m.userId} value={m.userId}>
                      {m.fullName || m.email}
                      {m.role !== 'contributor' && (
                        <span className="ml-2 text-meta">
                          ({m.role})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleConfirm()}
              disabled={!reviewerId || submitting || candidates.length === 0}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Send for review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
